import { VariantType } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { SWRConfiguration } from 'swr';

import {
  AbsenceStatus,
  LongAbsence,
  ShortAbsence,
  approveLongAbsence,
  approveShortAbsence,
} from '~/data/absences';
import { AdminWorkspaceRoles } from '~/data/user/role';
import {
  BulkApprovalAction,
  getFormattedDate,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import { ApprovalWidgetData } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/types';
import {
  AbsenceStatusEnum,
  AbsenceTypeEnum,
} from '~/services/Http/client.people.generated';
import { useGetTeamAbsences } from '~/services/Http/hooks';

type Props = {
  employeeId: number;
  userRoles: string[];
  widgetRoles?: AdminWorkspaceRoles[];
  showSnackBar: (
    responsesNumber: number,
    variant: VariantType,
    action: BulkApprovalAction,
  ) => void;
  enabled: boolean;
};

type UseApprovalWidgetAbsencesDataOutput = {
  isLoading: boolean;
  failedToLoad: boolean;
  data: {
    absences: ApprovalWidgetData[];
  };
  onDecision: (action: BulkApprovalAction) => Promise<void>;
  selection: ApprovalWidgetData[];
  setSelection: (selection: ApprovalWidgetData[]) => void;
};

export const useApprovalWidgetAbsencesData = (
  props: Props,
  swrConfig?: SWRConfiguration,
): UseApprovalWidgetAbsencesDataOutput => {
  const { employeeId, widgetRoles, showSnackBar, enabled } = props;

  const {
    data: teamAbsences,
    loading: absencesLoading,
    failedToLoad: absencesFailedToLoad,
    mutate,
  } = useGetTeamAbsences(
    {
      employeeId: 'me',
      roles: widgetRoles,
    },
    swrConfig,
    !enabled,
  );

  const absencesWidgetData = useMemo(() => {
    const proposedAbsences = teamAbsences?.filter(
      (a) => a.status.code === AbsenceStatusEnum.PROPOSED,
    );

    return proposedAbsences
      ? proposedAbsences.map(
          (absence) =>
            ({
              employee: absence.employee,
              type: absence.type,
              value: absence.reason.value,
              startDate: absence.startDate.toString(),
              endDate: absence.endDate || '',
              days: absence.days,
              id: absence.id.toString(),
              uid: absence.uid,
              info: absence.info,
            } as ApprovalWidgetData),
        )
      : [];
  }, [teamAbsences]);

  //
  const [selectedAbsences, setSelectedAbsences] = useState<
    ApprovalWidgetData[]
  >([]);

  const handleAbsencesApprove = useCallback(
    async (action: BulkApprovalAction) =>
      await Promise.all(
        selectedAbsences.map(async (absence) => {
          const teamAbsence = teamAbsences?.find(
            (ta) => ta.uid === absence.uid,
          );
          if (teamAbsences && teamAbsence) {
            if (absence.type === AbsenceTypeEnum.SHORT) {
              const modifiedAbsence: ShortAbsence = {
                fAbsenceShortAttachment: {
                  fAttachmentId: -1,
                  fAttachmentName: '',
                },
                fAbsenceShortDays: teamAbsence.days,
                fAbsenceShortDeciderEmpId: employeeId,
                fAbsenceShortEndDate: absence.endDate
                  ? getFormattedDate(absence.endDate, 'YYYY-MM-DD')
                  : undefined,
                fAbsenceShortEmpId: absence.employee.id,
                fAbsenceShortEnteredUserName: `${teamAbsence.employee.firstName} ${teamAbsence.employee.lastName}`,
                fAbsenceShortId: absence.id,
                fAbsenceShortInfo: absence.info,
                fAbsenceShortReason: teamAbsence.reason.code,
                fAbsenceShortStartDate: getFormattedDate(
                  absence.startDate,
                  'YYYY-MM-DD',
                ),
                fAbsenceShortStatus:
                  action === BulkApprovalAction.APPROVE
                    ? AbsenceStatus.APPROVED
                    : AbsenceStatus.REJECTED,
                fAbsenceShortUId: absence.uid,
              };
              const absencesProposalResponses = await approveShortAbsence(
                absence.employee.id,
                modifiedAbsence,
              );

              return {
                ...absencesProposalResponses,
                type: AbsenceTypeEnum.SHORT,
              };
            } else {
              const modifiedAbsence: LongAbsence = {
                fAbsenceLongAttachment: {
                  fAttachmentId: -1,
                  fAttachmentName: '',
                },
                fAbsenceLongDeciderEmpId: employeeId,
                fAbsenceLongEndDate: absence.endDate
                  ? getFormattedDate(absence.endDate, 'YYYY-MM-DD')
                  : undefined,
                fAbsenceLongEmpId: absence.employee.id,
                fAbsenceLongEnteredUserName: `${teamAbsence.employee.firstName} ${teamAbsence.employee.lastName}`,
                fAbsenceLongId: absence.id,
                fAbsenceLongInfo: absence.info,
                fAbsenceLongReason: teamAbsence.reason.code,
                fAbsenceLongStartDate: getFormattedDate(
                  absence.startDate,
                  'YYYY-MM-DD',
                ),
                fAbsenceLongStatus:
                  action === BulkApprovalAction.APPROVE
                    ? AbsenceStatus.APPROVED
                    : AbsenceStatus.REJECTED,
                fAbsenceLongUId: absence.uid,
              };
              const absencesProposalResponses = await approveLongAbsence(
                absence.employee.id,
                modifiedAbsence,
              );

              return {
                ...absencesProposalResponses,
                type: AbsenceTypeEnum.LONG,
              };
            }
          }

          return {};
        }),
      ),
    [employeeId, selectedAbsences, teamAbsences],
  );

  const handleAbsencesDecision = useCallback(
    async (action: BulkApprovalAction): Promise<void> => {
      const selectedAbsencesCount = selectedAbsences.length;
      return handleAbsencesApprove(action).then(() => {
        mutate();
        setSelectedAbsences([]);
        showSnackBar(selectedAbsencesCount, 'success', action);
      });
    },
    [
      selectedAbsences,
      setSelectedAbsences,
      handleAbsencesApprove,
      showSnackBar,
      mutate,
    ],
  );

  return {
    isLoading: absencesLoading,
    failedToLoad: absencesFailedToLoad,
    data: { absences: absencesWidgetData },
    onDecision: handleAbsencesDecision,
    selection: selectedAbsences,
    setSelection: setSelectedAbsences,
  };
};
